import { Component, inject, input, OnInit, output } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ActionColor, CheckBoxAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { SubrentalCutoffModel } from 'src/app/models/subrental/subrental-cutoff.model';
import { SubrentalCutoffDetailRegisterTable } from 'src/app/models/tables/subrental/subrental-cutoff-detail-register-table.model';
import { SubrentalCutoffDetailService } from 'src/app/services/subrental/subrental-cutoff-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-subrental-cutoff-detail',
  templateUrl: './subrental-cutoff-detail.component.html',
  styleUrls: ['./subrental-cutoff-detail.component.scss'],
  standalone: false
})
export class SubrentalCutoffDetailComponent implements OnInit {

  private readonly subrentalCutoffDetailService = inject(SubrentalCutoffDetailService);
  private readonly toastHandlerService = inject(ToastHandlerService);

  rowsEditable = input<boolean>(true);
  subrentalCutoffModel = input<SubrentalCutoffModel>(new SubrentalCutoffModel());
  updateSubrentalCutoffListDetail = input<boolean>(true);
  quantityDetails = output<number>();
  detailWithoutMovement = output<boolean>();

  ascendant: boolean = true;
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      color: ActionColor.DANGER,
      action: 'bulk-delete'
    }
  ];
  clearSelectionTrigger: number = 0;
  columnSort: string | null = null;
  detailData: PagedResponseModel<SubrentalCutoffDetailRegisterTable> = new PagedResponseModel<SubrentalCutoffDetailRegisterTable>();
  detailHeader: ColumnResponseModel[] = [];
  filters: ColumnResponseModel[] = []
  limit: number = 10;
  page: number = 1;
  visibleListDetail: boolean = false;

  async ngOnInit(): Promise<void> {
    await this.getColumns();
  }

  ngOnChanges(): void {
    if (this.subrentalCutoffModel().subrentalCutoffId !== 0 && this.subrentalCutoffModel().subrentalCutoffId !== -1) {
      this.filters = [];
      this.getSubrentalCutoffDetailsForRegisterTable();
    } else {
      this.visibleListDetail = false;
    }
  }

  async deleteSomeDetails(ids: number[]) {
    const result = await Swal.fire({
      title: 'Atención',
      text: "¿Realmente desea eliminar el detalle seleccionado del corte de subalquiler?",
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Si',
      cancelButtonText: 'No'
    });
    if (result.isConfirmed) {
      try {
        await firstValueFrom(this.subrentalCutoffDetailService.deleteSubrentalCutoffDetail(ids));
        this.toastHandlerService.showToast('success', 'El detalle del corte de subalquiler fue eliminado exitosamente.', 'Atención');
        this.clearSelectionTrigger++;
        this.getSubrentalCutoffDetailsForRegisterTable();
      } catch (error) {
        console.error('Error al eliminar el detalle del corte de subalquiler.', error);
      }
    }
  }

  async getColumns() {
    try {
      this.detailHeader = await firstValueFrom(this.subrentalCutoffDetailService.getColumnsForRegisterTable());
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  async getSubrentalCutoffDetailsForRegisterTable() {
    try {
      this.detailData = await firstValueFrom(this.subrentalCutoffDetailService.getSubrentalCutoffDetailsForRegisterTable(
        this.page, this.limit, this.columnSort, this.ascendant, this.subrentalCutoffModel().subrentalCutoffId, this.filters)
      );
      this.quantityDetails.emit(this.detailData.totalItems);
      if (this.detailData.totalItems != 0) {
        this.visibleListDetail = true;
      } else {
        this.visibleListDetail = false;
      }
    } catch (error) {
      console.error('Error al buscar los detalles del corte de subalquiler.', error);
    }
  }

  getUpdateListDetail(updateListDetail: boolean) {
    if (updateListDetail) {
      this.getSubrentalCutoffDetailsForRegisterTable();
    }
  }

  async onAction(event: { action: string, item?: SubrentalCutoffDetailRegisterTable, selectedItems?: number[] }) {
    if (event.action === 'bulk-delete' && event.selectedItems) {
      this.deleteSomeDetails(event.selectedItems);
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);

    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    this.getSubrentalCutoffDetailsForRegisterTable();
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter == null || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

}
